
import { CommonModule } from '@angular/common';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, Input, signal, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DetailsService } from './new-details.service';
import { HeaderDetailsComponent } from './components/header/header.component';
import { DetailsHeroComponent } from './components/hero/hero.component';
import { ActionButtonsDetailsLinksComponent } from './components/action-buttons/action-buttons.component';
import { DetailsDescriptionComponent } from './components/description/description.component';
import { DetailsDateRangeComponent } from './components/date-range/date-range.component';
import { DetailsLocationComponent } from './components/location/location.component';
import { DetailsScheduleComponent } from './components/schedule/schedule.component';
import { DetailsPriceComponent } from './components/price/price.component';
import { DetailsExtraFieldsComponent } from './components/extra-fields/extra-fields.component';
import { DetailsOffersComponent } from './components/offers/offers.component';
import { ToolsService } from 'src/app/services/tools.service';
import { IonicModule, ModalController } from '@ionic/angular';
import { UserService } from 'src/app/user/services';
import { AudioPlayerComponent } from 'src/app/shared/audio-player/audio-player.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { SelectDropdownComponent } from 'src/app/pages/new-details/components/select-dropdown/select-dropdown.component';
import { TagsComponent } from './components/tags/tags.component';
import { ModalCloseButtonComponent } from "./components/modal-close-button/modal-close-button.component";
import { GpxRouteComponent } from './components/gpx-route/gpx-route.component';

@Component({
    selector: 'item-details',
    imports: [
        CommonModule,
        IonicModule,
        TranslateModule,
        HeaderDetailsComponent,
        DetailsHeroComponent,
        ActionButtonsDetailsLinksComponent,
        DetailsDescriptionComponent,
        DetailsDateRangeComponent,
        DetailsLocationComponent,
        DetailsScheduleComponent,
        DetailsPriceComponent,
        DetailsExtraFieldsComponent,
        DetailsOffersComponent,
        AudioPlayerComponent,
        SelectDropdownComponent,
        TagsComponent,
        ModalCloseButtonComponent,
        GpxRouteComponent
    ],
    standalone: true,
    templateUrl: './new-details.page.html',
    styleUrls: ['./new-details.page.scss'],
    animations: [
        trigger('fadeSlide', [
            transition(':enter', [
                style({ transform: 'translateY(120px)' }),
                animate('500ms cubic-bezier(0.22, 1, 0.36, 1)',  // easeOutCubic
                    style({ transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('250ms cubic-bezier(0.4, 0, 0.2, 1)',     // Material standard
                    style({ opacity: 0, transform: 'translateY(16px)' }))
            ])
        ])
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    encapsulation: ViewEncapsulation.None,
})

export class NewDetailsPage {
    @Input("isModal") isModal: boolean = false;
    @Input("item") item?: any;
    @Input("addItem") addItem?: any;
    @Input("removeItem") removeItem?: any;

    private routerService = inject(Router);
    private routeService = inject(ActivatedRoute);
    private detailsService = inject(DetailsService);
    private userService = inject(UserService);
    private itemId: string;

    private modalController = inject(ModalController);
    public tools = inject(ToolsService);
    public translate = inject(TranslateService);
    public currentUrl = computed(() => this.routerService.url);
    public itemDetails = computed(() => this.detailsService.itemDetails());
    public gpxData = computed(() => this.detailsService.gpxData());

    public gpxFile = signal(null);
    public audioSrc = signal(null);

    ngOnInit(){
        this.itemId = this.routeService.snapshot.paramMap.get('itemId');
        this.userService.checkAllUserEntries();
        this.initializeDetailsItem();
    }

    private initializeDetailsItem() {
        this.detailsService.setIsModal(this.isModal);
        
        if (this.currentUrl().includes('/event/') || this.item?.type === 'event')
            this.detailsService.getEventDetailsServiceSignal(this.itemId || this.item?.id)

        if (this.currentUrl().includes('/point/') || this.item?.type === 'point')
            this.detailsService.getPointDetailsServiceSignal(this.itemId || this.item?.id)

        if (this.currentUrl().includes('/hiking/') || this.item?.type === 'hiking')
            this.detailsService.getHikingDetailsServiceSignal(this.itemId || this.item?.id)
    }

    downloadGpxFile(){
        const a = document.createElement('a');
        const url = this.itemDetails()?.gpxFile?.[this.translate.currentLang]?.[0]?.src;
        if (!url)
            return;

        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    downloadKmzFile(){
        const a = document.createElement('a');
        const url = this.itemDetails()?.kmzFile?.[this.translate.currentLang]?.[0]?.src;
        if (!url)
            return;

        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    acceptItem(){
        this.modalController.dismiss(true);
    }

    closeAudioPlayer(){
        this.audioSrc.set(null);
    }

    setAudio(src){
        this.audioSrc.set(src);
    }

    ngOnDestroy(){
        this.closeAudioPlayer();
        this.detailsService.resetItemDetails();
    }
}